import { Box, Typography } from "@mui/material";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import AppButton from "../../../../base/AppButton.component";
import { useVerification } from "../../../../../context/Verification.context";
import { WARRANTY_VEHICLE_TYPE_ID } from "../../../admin/dealership/tabs/warrantyConstants";

const MESSAGES = {
  en: "If you are on a mobile device, please rotate device to landscape after clicking Start.",
  es: "Si utiliza un dispositivo móvil, por favor gírelo a posición horizontal después de hacer clic en «Iniciar».",
};

// Maps the engine key stored from VehicleTransactionResponse's Engine dropdown
// to the warranty module's vehicle type id.
const ENGINE_TO_WARRANTY_TYPE_ID = {
  GAS: WARRANTY_VEHICLE_TYPE_ID.GAS,
  HYBRID: WARRANTY_VEHICLE_TYPE_ID.HYBRID,
  EV: WARRANTY_VEHICLE_TYPE_ID.EV,
  DIESEL: WARRANTY_VEHICLE_TYPE_ID.DIESEL,
};

const NO_VEHICLE_TYPE_MESSAGES = {
  en: "Please select your vehicle's engine type in the Vehicle & Transaction step before starting the Warranty Module.",
  es: "Seleccione el tipo de motor de su vehículo en el paso de Vehículo y Transacción antes de iniciar el Módulo de Garantía.",
};

// Short-height landscape phones (the "rotate to landscape" case itself) need
// everything to shrink so the card fits without overlapping the logo/header
// above it. Width-based MUI breakpoints (xs/sm) don't catch this because the
// device is often "sm"+ wide once rotated — the constraint is height.
const LANDSCAPE_SHORT = "@media (orientation: landscape) and (max-height: 500px)";
// Even shorter (common for phones like iPhone SE/mini in landscape, ~320-360px
// tall) — needs a second, more aggressive shrink pass on top of the above.
const LANDSCAPE_VERY_SHORT = "@media (orientation: landscape) and (max-height: 380px)";

/**
 * Single in-page card: rotate-phone message + Start/Stop buttons.
 * Renders directly inside the artifact content area (like survey/text responses).
 */
const WarrantyStartPopup = ({
  onStart,
  onStop,
  loading = false,
}) => {
  const verification = useVerification();
  const isSpanish = Boolean(verification?.aiData?.spanish);
  const message = isSpanish ? MESSAGES.es : MESSAGES.en;
  const noVehicleTypeMessage = isSpanish
    ? NO_VEHICLE_TYPE_MESSAGES.es
    : NO_VEHICLE_TYPE_MESSAGES.en;

  const engineKey =
    verification?.artifactsResponse?.vehicleTransactionResponse?.engine ||
    null;
  const selectedTypeId = engineKey
    ? ENGINE_TO_WARRANTY_TYPE_ID[engineKey] || null
    : null;

  const handleStop = () => {
    onStop?.();
  };

  const handleStart = () => {
    if (!selectedTypeId || loading) return;
    onStart?.(selectedTypeId);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        overflowY: "auto",
        p: { xs: 2.5, sm: 3 },
        maxWidth: 420,
        mx: "auto",
        my: "auto",
        [LANDSCAPE_SHORT]: {
          gap: 0.4,
          p: 0.5,
        },
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          backgroundColor: "rgba(0, 171, 85, 0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          [LANDSCAPE_SHORT]: {
            width: 24,
            height: 24,
          },
          [LANDSCAPE_VERY_SHORT]: {
            display: "none",
          },
        }}
      >
        <ScreenRotationIcon
          sx={{
            fontSize: 36,
            color: "#00ab55",
            [LANDSCAPE_SHORT]: { fontSize: 14 },
          }}
        />
      </Box>

      <Typography
        variant="h6"
        fontWeight={700}
        color="#212121"
        sx={{
          lineHeight: 1.2,
          [LANDSCAPE_SHORT]: { fontSize: "0.75rem" },
        }}
      >
        {isSpanish ? "Módulo de Garantía" : "Warranty Module"}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          px: 1,
          lineHeight: 1.2,
          [LANDSCAPE_SHORT]: { fontSize: "0.6rem", px: 0.5 },
        }}
      >
        {message}
      </Typography>

      {!selectedTypeId && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            px: 1,
            lineHeight: 1.2,
            [LANDSCAPE_SHORT]: { fontSize: "0.55rem", px: 0.5 },
          }}
        >
          {noVehicleTypeMessage}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          width: "100%",
          mt: 1,
          justifyContent: "center",
          [LANDSCAPE_SHORT]: { mt: 0.25, gap: 0.75 },
        }}
      >
        <AppButton
          buttonText={isSpanish ? "Detener" : "Stop"}
          variant="outlined"
          size="medium"
          clickHandler={handleStop}
          sx={{
            minWidth: 110,
            flex: 1,
            [LANDSCAPE_SHORT]: {
              minWidth: 56,
              minHeight: 22,
              fontSize: "0.6rem",
              px: 1,
              py: 0.25,
            },
          }}
        />
        <AppButton
          buttonText={isSpanish ? "Iniciar" : "Start"}
          variant="contained"
          size="medium"
          clickHandler={handleStart}
          disabled={!selectedTypeId || loading}
          loading={loading}
          sx={{
            minWidth: 110,
            flex: 1,
            [LANDSCAPE_SHORT]: {
              minWidth: 56,
              minHeight: 22,
              fontSize: "0.6rem",
              px: 1,
              py: 0.25,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default WarrantyStartPopup;